import type { InputAdapter, InputCapabilities } from './InputAdapter';

/**
 * Who is driving input right now, and was the last activation a real user gesture.
 *
 * `specs/10-contracts.md` requires `TtsContext.inGesture` but never says who computes it (flagged as
 * C18). This is that answer, and it deserves to be one module rather than a guess at each call
 * site, because the cost of getting it wrong is invisible: `speechSynthesis.speak()` outside a
 * gesture is *silently dropped*, so a wrong answer here means the person taps, sees their
 * sentence, and nothing is ever said.
 *
 * Two independent conditions, both required:
 *
 *  - the active adapter is not `isTimed` — a dwell timer or auto-scan tick is not a gesture, no
 *    matter how recently the person touched the screen;
 *  - a real gesture happened very recently — Safari's own allowance is short and undocumented, so
 *    this is deliberately tighter than it needs to be.
 */

const GESTURE_WINDOW_MS = 1000;

const DIRECT_TOUCH_CAPABILITIES: InputCapabilities = {
  needsFocusRing: false,
  isTimed: false,
  needsAudioCue: false,
};

let active: InputAdapter | null = null;
let lastGestureAtMs = Number.NEGATIVE_INFINITY;
let explicitGestureAtMs = Number.NEGATIVE_INFINITY;

export function setActiveAdapter(adapter: InputAdapter | null): void {
  active = adapter;
}

export function activeCapabilities(): InputCapabilities {
  return active?.capabilities ?? DIRECT_TOUCH_CAPABILITIES;
}

/** Called by adapters from a genuine user event, never from a timer. */
export function noteUserGesture(): void {
  lastGestureAtMs = typeof performance === 'undefined' ? 0 : performance.now();
}

/**
 * A press on one specific control, by a finger, which counts as a gesture **whatever the input mode**.
 *
 * ── Why this exists (C64) ──
 *
 * The rule below was too coarse and shipped a promise it could not keep. `isTimed` means *activations produced
 * by this adapter are timer-driven*; it was being read as *nothing that happens in this mode is a gesture*. So
 * in dwell mode, tapping `Antippen zum Sprechen` — the whole point of which is that the tap **is** a gesture,
 * so the platform voice becomes permitted for one utterance — was refused by our own check, fell through to
 * `visualOnly` again, and did nothing. The button was theatre in exactly the mode it was built for.
 *
 * Deliberately narrow: one caller, one control, one reason. It is not "override the rule when convenient" —
 * it is "a human just pressed this, with their finger, and Safari agrees that is a gesture".
 */
export function noteExplicitGesture(): void {
  explicitGestureAtMs = typeof performance === 'undefined' ? 0 : performance.now();
}

export function inGesture(): boolean {
  const now = typeof performance === 'undefined' ? 0 : performance.now();

  // Checked before the adapter rule, because an explicit press outranks the mode.
  if (now - explicitGestureAtMs <= GESTURE_WINDOW_MS) return true;

  if (activeCapabilities().isTimed) return false;
  return now - lastGestureAtMs <= GESTURE_WINDOW_MS;
}

/** Test seam. */
export function resetGestureStateForTests(): void {
  active = null;
  lastGestureAtMs = Number.NEGATIVE_INFINITY;
  explicitGestureAtMs = Number.NEGATIVE_INFINITY;
}
